import { toast } from "react-toastify"
import { DS, StatGroup } from "../ds/Kit"
import { Box, HBox } from "@ui/box/Box"
import Button from "@ui/button/Button"
import Input from "@ui/input/Input"
import { Select } from "@ui/select/Select"
import Spacer from "@ui/spacer/Spacer"
import { Tile } from "@ui/tile/Tile"
import { useCallback, useEffect, useState } from "react"
import { GD } from "src/GD"
import { CardDetails } from "../components/CardDetails"
import { CARD_SORT_OPTIONS, CardsList } from "../components/CardsList"
import { DEFAULT_SORT, sortSelectValues } from "src/utils/listSort"
import { S_UNIVERSAL_DIALOG } from "../components/universalDialog/signals"
import { CardIssuanceForm } from "../components/CardIssuanceForm"
import { TUser, USER_ROLE } from "src/services/AuthService"
import { Page } from "@ui/page/Page"
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter"
import { CARD_ISSUANCE_FEE, DEFAULT_CURRENCY } from "src/constants"
import { useWindowSize } from "@ui/utils/useResize"
import { useIssuer } from "../issuer/IssuerProvider"
const CARD_STATUS_OPTIONS = [
	"All statuses",
	"active",
	"frozen",
	"blocked",
	"inactive",
	"pending",
	"created",
	"closed",
	"lost",
	"replaced",
	"unavailable",
	"declined",
	"error"
]

export const CardsPage = () => {
	const { width: viewportWidth } = useWindowSize()
	const { selectedIssuer, selectedIssuerId } = useIssuer()
	const issuerCurrency = selectedIssuer?.currency || DEFAULT_CURRENCY
	const isMobile = viewportWidth <= 600
	const label = "Cards"
	const [filtersExpanded, setFiltersExpanded] = useState(false)
	const [view, setView] = useState<"grid" | "list">(() => {
		try { return localStorage.getItem("cards-view") === "list" ? "list" : "grid" } catch { return "grid" }
	})
	const listView = isMobile ? "grid" : view
	const changeView = (next: "grid" | "list") => {
		setView(next)
		try { localStorage.setItem("cards-view", next) } catch { /* per-viewer convenience only */ }
	}
	const [forceUpdate] = useState(0)
	const [userProfile, setUserProfile] = useState<TUser | null>(null)

	const [filter, setFilter] = useState({
		name: "",
		code: "",
		currency: "",
		country: "",
		status: "",
		sort: ""
	})
	const [userId, setUserId] = useState("")
	const [companyId, setCompanyId] = useState("")
	// const [cardHolders, setCardHolders] = useState<ICardHolderSaved[]>([]);

	const [filterOptions, setFilterOptions] = useState({
		countries: [] as string[],
		currencies: [] as string[],
		networks: [] as string[]
	})

	// Totals of every card matching the filters, reported by the list.
	const [summary, setSummary] = useState<ICardListSummary | null>(null)

	useEffect(() => {
		const params = new URLSearchParams(window.location.search)
		const uId = params.get("userUid")
		const compId = params.get("companyUid")
		const status = params.get("status")
		if (uId) setUserId(uId)
		if (compId) setCompanyId(compId)
		if (status) {
			setFilter((prev) => ({ ...prev, status }))
		}

		const updateProfile = async () => {
			const profile = await GD.REQ_PROFILE.request()
			if (profile) setUserProfile(profile || null)

			// const cardHoldersRes = await GD.REQ_CARDHOLDERS.request();
			// if (cardHoldersRes.data) setCardHolders(cardHoldersRes.data);
		}
		updateProfile()
	}, [])

	useEffect(() => {
		// ponytail: the backend has no facets endpoint, so filter choices come from one full read of the cards.
		const loadFilterOptions = async () => {
			const res = await GD.REQ_CARDS.request()
			if (res.data) {
				const cards = res.data
				const availableCurrencies = Array.from(
					new Set(cards.map((c) => c.currency).filter(Boolean))
				) as string[]
				const availableCountries = Array.from(
					new Set(cards.map((c) => c.country_code).filter(Boolean))
				) as string[]

				const availableNetworks = Array.from(
					new Set(cards.map((c) => c.network).filter(Boolean))
				)
				const networks: string[] = []
				if (availableNetworks.includes(1)) networks.push("Visa")
				if (availableNetworks.includes(3)) networks.push("Mastercard")

				setFilterOptions({
					currencies: availableCurrencies,
					countries: availableCountries,
					networks: networks
				})
			}
		}
		loadFilterOptions()
	}, [forceUpdate, selectedIssuerId])

	useEffect(() => () => S_UNIVERSAL_DIALOG.invoke(null), [])

	const handleSelectCard = (card: ICardSaved) => {
		S_UNIVERSAL_DIALOG.invoke({
			label: "Card Details",
			closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
			size: "large",
			// The dialog already scrolls and caps its own height. A second wrapper sized
			// to the viewport (85vh) made every sub-view keep a different amount of dead
			// space under its buttons, so the content goes in directly.
			content: (
				<CardDetails
					card={card}
					onClose={() => S_UNIVERSAL_DIALOG.invoke(null)}
				/>
			)
		})
	}

	const openCreateCardDialog = useCallback(() => {
		S_UNIVERSAL_DIALOG.invoke({
			closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
			label: "Create Card",
			content: (
				<Box data-dialog-layout='compact-form' sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
					<CardIssuanceForm
						context={{ issuer: selectedIssuerId ?? undefined }}
						primaryTitle='Create Card'
						defaultIssuanceFee={CARD_ISSUANCE_FEE}
						confirmMessage={feeDisplay =>
							`This will issue a real card and charge a ${feeDisplay} issuance fee to the company balance.`
						}
						onCreated={feeDisplay => {
							toast.success(
								`Card request submitted. ${feeDisplay} issuance fee charged. Card activation is in progress.`
							)
							S_UNIVERSAL_DIALOG.invoke(null)
						}}
					/>
				</Box>
			)
		})
	}, [selectedIssuerId])

	const handleCreateCard = openCreateCardDialog
	const hideBuyer = userProfile?.company_role === USER_ROLE.BUYER
	const activeFilterCount = [filter.code, filter.currency, filter.country, filter.status].filter(Boolean).length

	const handleReset = useCallback(() => {
		setFilter({ name: "", code: "", currency: "", country: "", status: "", sort: "" })
		setUserId("")
	}, [])

	return (
		<Page
			sx={{ boxShadow: "none" }}
			label={label}
			description='Issue, track and control your company cards.'
			sectionRightContent={
				userProfile?.company_role !== USER_ROLE.TEAM_LEAD && selectedIssuerId !== null && (
					<Button onClick={handleCreateCard} title='Add New Card' variant='primarySmall' />
				)
			}
		>
			<StatGroup
				items={[
					{ label: "Cards", value: summary ? summary.cards : "—", hint: summary ? `${summary.active} active` : undefined },
					{ label: "Total card balance", value: summary ? formatCurrencyWithNumberToSymbolWithNumber(summary.balance, issuerCurrency) : "—" },
					{ label: "Monthly turnover", value: "—", hint: "Not available yet" },
					{ label: "Avg. balance per card", value: summary ? formatCurrencyWithNumberToSymbolWithNumber(summary.cards > 0 ? summary.balance / summary.cards : 0, issuerCurrency) : "—" }
				]}
			/>

			<Spacer size='20px' />

			<HBox
				gap='10px'
				sx={{
					alignItems: "flex-end",
					flexWrap: "wrap",
					padding: "12px",
					borderRadius: DS.radius,
					border: `1px solid ${DS.line}`,
					background: DS.surface,
					boxShadow: DS.shadow,
					"& > *": { flex: isMobile ? "1 1 100%" : undefined }
				}}
			>
					<Input
						label='Search cards'
						sx={{ width: isMobile ? "100%" : "auto", flex: isMobile ? "1 1 100%" : "1 1 240px", minWidth: 0 }}
						placeholder='Card name or number...'
						data={filter}
						name='name'
						onChange={(value) => setFilter((prev) => ({ ...prev, name: value }))}
					/>
					{isMobile && (
						<Box element='button' type='button' sx={{ width: "100%", minHeight: "44px", padding: "10px 16px", border: "1px solid #E3E7EF", borderRadius: "12px", backgroundColor: "#fff", color: "#17243B", fontFamily: "inherit", fontSize: ".95rem", fontWeight: 600, cursor: "pointer" }}
							aria-expanded={filtersExpanded}
							onClick={() => setFiltersExpanded((expanded) => !expanded)}>
							{filtersExpanded ? "Hide filters" : "More filters"}{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ""}
						</Box>
					)}
					{(!isMobile || filtersExpanded) && <Box sx={{ display: "contents", "& > *": { animation: isMobile ? "v2-enter .24s ease both" : "none" } }}>
					<Select
						label='Card network'
						sx={{ minWidth: isMobile ? "100%" : "180px", width: isMobile ? "100%" : "auto", flex: isMobile ? "1 1 100%" : "0 0 auto" }}
						data={{ code: filter.code || "All networks" }}
						name='code'
						values={["All networks", ...filterOptions.networks]}
						onChange={(value) =>
							setFilter((prev) => ({
								...prev,
								code: value === "All networks" ? "" : (value as string)
							}))
						}
					/>
					<Select
						label='Currency'
						sx={{ minWidth: isMobile ? "100%" : "120px", width: isMobile ? "100%" : "auto", flex: isMobile ? "1 1 100%" : "0 0 auto" }}
						data={{ currency: filter.currency || "All currencies" }}
						name='currency'
						values={["All currencies", ...filterOptions.currencies]}
						onChange={(value) =>
							setFilter((prev) => ({
								...prev,
								currency: value === "All currencies" ? "" : (value as string)
							}))
						}
					/>
					<Select
						label='Country'
						sx={{ minWidth: isMobile ? "100%" : "120px", width: isMobile ? "100%" : "auto", flex: isMobile ? "1 1 100%" : "0 0 auto" }}
						data={{ country: filter.country || "All countries" }}
						name='country'
						values={["All countries", ...filterOptions.countries]}
						onChange={(value) =>
							setFilter((prev) => ({
								...prev,
								country: value === "All countries" ? "" : (value as string)
							}))
						}
					/>
					<Select
						label='Status'
						sx={{ minWidth: isMobile ? "100%" : "140px", width: isMobile ? "100%" : "auto", flex: isMobile ? "1 1 100%" : "0 0 auto" }}
						data={{ status: filter.status || "All statuses" }}
						name='status'
						values={CARD_STATUS_OPTIONS}
						onChange={(value) =>
							setFilter((prev) => ({
								...prev,
								status: value === "All statuses" ? "" : (value as string)
							}))
						}
					/>
						<Select
							label='Sort by'
							sx={{ minWidth: isMobile ? "100%" : "180px", width: isMobile ? "100%" : "auto", flex: isMobile ? "1 1 100%" : "0 0 auto" }}
							data={{ sort: filter.sort || DEFAULT_SORT }}
							name='sort'
							values={sortSelectValues(hideBuyer ? CARD_SORT_OPTIONS.filter(option => option.sort !== "owner") : CARD_SORT_OPTIONS)}
							onChange={(value) => setFilter((prev) => ({ ...prev, sort: value === DEFAULT_SORT ? "" : String(value) }))}
						/>
					</Box>}
					{(activeFilterCount > 0 || filter.name) && (
						<Box
							element='button'
							type='button'
							onClick={handleReset}
							sx={{ border: 0, background: "transparent", color: DS.accent, fontFamily: "inherit", fontSize: "14px", fontWeight: 600, cursor: "pointer", minHeight: "44px", padding: "0 8px" }}
						>
							Reset
						</Box>
					)}
					{!isMobile && (
						<HBox role='group' aria-label='Card layout' sx={{ marginLeft: "auto", padding: "3px", gap: "2px", borderRadius: "11px", background: DS.soft, border: `1px solid ${DS.line}` }}>
							{(["grid", "list"] as const).map(option => (
								<Box
									key={option}
									element='button'
									type='button'
									aria-pressed={view === option}
									onClick={() => changeView(option)}
									sx={{ minHeight: "36px", padding: "0 14px", border: 0, borderRadius: "8px", fontFamily: "inherit", fontSize: "13px", fontWeight: 600, cursor: "pointer", background: view === option ? DS.surface : "transparent", color: view === option ? DS.ink : DS.muted, boxShadow: view === option ? "0 1px 3px rgba(15, 23, 42, 0.12)" : "none" }}
								>
									{option === "grid" ? "Cards" : "Table"}
								</Box>
							))}
						</HBox>
					)}
			</HBox>

			<Spacer size='20px' />

			<Box sx={{ overflow: "hidden" }}>
				<Tile
					sx={{
						...(listView === "grid" ? { backgroundColor: "transparent", boxShadow: "none", border: 0, borderRadius: 0 } : {}),
						padding: "0px",
						overflow: "auto",
						display: "flex",
						flexDirection: "column",
						height: "100%"
					}}
				>
					<CardsList
						userUid={userId}
						hideBuyer={hideBuyer}
						onSummary={setSummary}
						companyUid={companyId}
						dependency={forceUpdate}
						filter={{
							order_id: filter.name,
							code: filter.code,
							status: filter.status,
							currency: filter.currency,
							country: filter.country,
							sort: filter.sort
						}}
						onSelect={handleSelectCard}
						view={listView}
					/>
				</Tile>
			</Box>
		</Page>
	)
}
